import { getImageDimensions } from './imageDimensions';
import { TemplateType } from '../../../../store/types/template';

export default function getCroppedImg(
  imageSrc: string,
  containerEl: HTMLElement,
  platform?: 'shared' | 'apple' | 'google' | 'enhancedEventTicket',
  field?: string,
  templateType?: TemplateType,
  expressivePassEnabled?: boolean
): Promise<string> {
  const imgEl = containerEl.querySelector(
    '.reactEasyCrop_Image'
  ) as HTMLImageElement | null;
  const cropAreaEl = containerEl.querySelector(
    '.reactEasyCrop_CropArea'
  ) as HTMLElement | null;

  if (!imgEl || !cropAreaEl) {
    return Promise.reject(new Error('Could not find crop elements'));
  }

  const imgRect = imgEl.getBoundingClientRect();
  const cropRect = cropAreaEl.getBoundingClientRect();

  if (imgRect.width === 0 || imgRect.height === 0) {
    return Promise.reject(new Error('Image has zero dimensions'));
  }

  const overlapLeft = Math.max(cropRect.left, imgRect.left);
  const overlapTop = Math.max(cropRect.top, imgRect.top);
  const overlapRight = Math.min(cropRect.right, imgRect.right);
  const overlapBottom = Math.min(cropRect.bottom, imgRect.bottom);

  const scaleX = imgEl.naturalWidth / imgRect.width;
  const scaleY = imgEl.naturalHeight / imgRect.height;

  const sx = (overlapLeft - imgRect.left) * scaleX;
  const sy = (overlapTop - imgRect.top) * scaleY;
  const sw = (overlapRight - overlapLeft) * scaleX;
  const sh = (overlapBottom - overlapTop) * scaleY;

  const fullCropW = cropRect.width * scaleX;
  const fullCropH = cropRect.height * scaleY;

  if (fullCropW === 0 || fullCropH === 0) {
    return Promise.reject(new Error('Crop area has zero dimensions'));
  }

  const dx = (overlapLeft - cropRect.left) * scaleX;
  const dy = (overlapTop - cropRect.top) * scaleY;

  const image = new Image();
  if (/^https?:\/\//i.test(imageSrc)) {
    image.crossOrigin = 'anonymous';
  }
  image.src = imageSrc;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  return new Promise((resolve, reject) => {
    image.onload = () => {
      let outputWidth = Math.round(fullCropW);
      let outputHeight = Math.round(fullCropH);

      const dimensions = getImageDimensions(templateType, { expressivePassEnabled });
      const maxDims =
        platform && field ? dimensions[platform]?.[field] : undefined;
      if (maxDims) {
        const scale = Math.min(
          maxDims.width / outputWidth,
          maxDims.height / outputHeight,
          1
        );
        outputWidth = Math.floor(outputWidth * scale);
        outputHeight = Math.floor(outputHeight * scale);
      }

      const outScale = fullCropW === 0 ? 0 : outputWidth / fullCropW;

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      ctx.drawImage(
        image,
        sx,
        sy,
        sw,
        sh,
        dx * outScale,
        dy * outScale,
        sw * outScale,
        sh * outScale
      );

      resolve(canvas.toDataURL('image/png', 0.92));
    };

    image.onerror = (error) => reject(error);
  });
}
